import classes from './ScheduleView.module.scss';
import React, {useLayoutEffect, useState} from 'react'
import {Tabs} from "antd";
import ScheduleList, {Mode} from "../ScheduleList/ScheduleList";
import ScheduleTargetDetailsCard from "./ScheduleTargetDetailsCard/ScheduleTargetDetailsCard";
import {getStartOfEducationTerm, splitExamsByMonth} from "../../../services/scheduleService";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import gsap from 'gsap';
import ScheduleFiltersDesktop from "../ScheduleFilters/Desktop/ScheduleFiltersDesktop";
import {getAllowedPeriodOrder, getDefaultPeriodTab, getPeriodLabels} from "./common";
import useDefaultWeek from '../ScheduleFilters/useDefaultWeek';
import ScheduleFiltersMobile from "../ScheduleFilters/Mobile/ScheduleFiltersMobile";
import MobileScheduleGrid from "../MobileScheduleGrid/MobileScheduleGrid";
import ExamScheduleGrid from "../ExamScheduleGrid/ExamScheduleGrid";
import ScheduleGrid from "../ScheduleGrid/ScheduleGrid";

const { TabPane } = Tabs;


export const ViewOption = {
    List: 1,
    Table: 2,
    Mobile: 3
};

function ScheduleView({viewMode, scheduleType, scheduleTarget, currentTermLessons, previousTermLessons,
                          exams, todayTomorrowLessons, currentPeriod, updateFilter, filters = {}}) {

    const {subgroupFilter, passedLessonsFilter, sessionFilter,
        showTodayTomorrowFilter} = filters;

    const [activeTab, setActiveTab] = useState(getDefaultPeriodTab(currentPeriod));

    const startOfEducationTerm = getStartOfEducationTerm(scheduleTarget);
    const defaultWeek = useDefaultWeek(startOfEducationTerm);

    useLayoutEffect(() => {
        if (viewMode !== ViewOption.List) return;
        const mm = gsap.matchMedia()
        mm.add('(min-width: 800px)', () => {
            ScrollTrigger.create({
                trigger: `.${classes.controls}`,
                start: 'top 20px',
                endTrigger: `.${classes.schedule}`,
                end: () => {
                    const schedule = document.querySelector(`.${classes.schedule}`);
                    const controlsSection = document.querySelector(`.${classes.controls}`);

                    if (schedule && controlsSection) {
                        return `bottom top+=${controlsSection.offsetHeight + 20}`;
                    }
                    return null;
                },
                pin: true
            });
        });
        const scheduleSizeObserver = new ResizeObserver((entries) => {
            if (entries && entries.length > 0) {
                ScrollTrigger.refresh();
            }
        });
        scheduleSizeObserver.observe(document.querySelector(`.${classes.schedule}`));

        return () => mm.revert();
    }, [viewMode]);

    const displayCurrentLessons = Object.entries(currentTermLessons ?? {}).length !== 0;
    const displayExams = Object.entries(exams ?? {}).length !== 0;
    const displayPrevLessons = Object.entries(previousTermLessons ?? {}).length !== 0;

    const getFiltersElement = () => {
        const activeTabLabel = getPeriodLabels(currentTermLessons, previousTermLessons)
            .find(l => l.key === activeTab)?.label;

        const showSubgroupFilters = activeTab === 'lessons'
            ? currentTermLessons?.hasSchedulesBySubgroups :
            previousTermLessons?.hasSchedulesBySubgroups;

        const props = {
            subgroupFilters: subgroupFilter,
            passedLessonsFilter: passedLessonsFilter,
            sessionFilter: sessionFilter,
            ...(viewMode === ViewOption.List && {showTodayAndTomorrowFilter: showTodayTomorrowFilter}),
            setFilter: updateFilter,
            ...(viewMode !== ViewOption.Mobile && {viewMode}),
            ...(viewMode === ViewOption.Mobile && {showSubgroupFilters}),
            lessonType: activeTab,
            isCurrentPeriod: currentPeriod ? activeTabLabel === currentPeriod : null,
            startOfTermDate: startOfEducationTerm,
            defaultWeek,
            scheduleType
        }

        return viewMode !== ViewOption.Mobile ?
            <ScheduleFiltersDesktop {...props}/>
            : <ScheduleFiltersMobile {...props}/>;
    }

    const getTabLabel = (key) => {
        const periodLabel = getPeriodLabels(currentTermLessons, previousTermLessons).find(l => l.key === key);
        const isCurrent = periodLabel.label && periodLabel.label === currentPeriod;

        return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span>{periodLabel.label}</span>
            {isCurrent &&
            <span style={{fontSize: '12px'}}>сейчас</span>}
        </div>);
    }

    const lessonsElementCreator = (lessons) => {
        const isCurrentPeriod = currentPeriod ? lessons.term === currentPeriod : undefined;
        if (viewMode === ViewOption.List) {
            return (<ScheduleList
                lessonsData={lessons.schedules}
                isCurrentPeriod={isCurrentPeriod}
                scheduleType={scheduleType}
                startOfTermDate={startOfEducationTerm}/>);
        }
        if (viewMode === ViewOption.Table) {
            return (<ScheduleGrid
                scheduleData={lessons.schedules}
                scheduleType={scheduleType}
                scheduleItemType='lesson'
                isCurrentPeriod={isCurrentPeriod}
                startOfTermDate={startOfEducationTerm}/>);
        }
        if (viewMode === ViewOption.Mobile) {
            return (<MobileScheduleGrid
                ifMobileGrid
                isCurrentPeriod={currentPeriod ? lessons.term === currentPeriod : null}
                scheduleData={lessons.schedules}
                scheduleType={scheduleType}
                scheduleItemType='lesson'
                startOfTermDate={startOfEducationTerm}/>);
        }
        return null;
    }

    const examsElementCreator = () => {
        if (viewMode === ViewOption.List) {
            return (<ScheduleList
                mode={Mode.exams}
                lessonsData={exams}
                scheduleType={scheduleType}/>);
        }
        if (viewMode === ViewOption.Table || viewMode === ViewOption.Mobile) {
            return (<ExamScheduleGrid
                ifMobileGrid
                schedule={splitExamsByMonth(exams)}
                scheduleType={scheduleType}
                scheduleItemType='exam'
                rawExams={exams} />);
        }
        return null;
    }

    const getTabbedContent = () => {
        const renderTabBar = (props, DefaultTabBar) => (
            <div className={classes.tabsAndFilters}>
                <DefaultTabBar {...props} />
                <div style={{minWidth: '0'}}>
                    {getFiltersElement()}
                </div>
            </div>);

        const periodData = [
            {
                key: 'lessons',
                display: displayCurrentLessons,
                getElement: () => lessonsElementCreator(currentTermLessons)
            },
            {
                key: 'exams',
                display: displayExams,
                getElement: () => examsElementCreator()
            },
            {
                key: 'previousLessons',
                display: displayPrevLessons,
                getElement: () => lessonsElementCreator(previousTermLessons)
            }
        ];

        const getSchedulePeriodSorted = () => {
            const periodOrder = getAllowedPeriodOrder(displayCurrentLessons, displayPrevLessons, displayExams)
                .find(po => po.shouldApply);
            if (!periodOrder) return periodData;

            return periodData.sort((a, b) => {
                return periodOrder.order[a.key] - periodOrder.order[b.key];
            });
        }

        const periodTabsToDisplay = getSchedulePeriodSorted().filter(p => p.display);
        if (periodTabsToDisplay.length === 1 && activeTab !== periodTabsToDisplay[0].key) {
            setActiveTab(periodTabsToDisplay[0].key);
        }

        return (<Tabs className={classes.periodTabs}
                      defaultActiveKey={activeTab}
                      onChange={(activeKey) => setActiveTab(activeKey)}
                      renderTabBar={viewMode === ViewOption.Table ? renderTabBar : null}>
            {
                periodTabsToDisplay.map(period =>
                    <TabPane tab={getTabLabel(period.key)} key={period.key}>
                        {period.getElement()}
                    </TabPane>)
            }
        </Tabs>);
    }

    const getControlsElement = () => {
        if (viewMode === ViewOption.Table) {
            return (<div className={classes.scheduleTargetDetailsContainer}>
                <ScheduleTargetDetailsCard
                    viewMode={ViewOption.Table}
                    scheduleType={scheduleType}
                    scheduleTarget={scheduleTarget}/>
            </div>);
        }
        return (<div className={classes.controls}>
            <ScheduleTargetDetailsCard
                scheduleType={scheduleType}
                scheduleTarget={scheduleTarget}/>
            {getFiltersElement()}
        </div>);
    }

    const getListOrMobileLayout = () => {
        return (<div className={classes.scheduleListContainer}>
            <div className={classes.schedule}>
                {(showTodayTomorrowFilter && viewMode !== ViewOption.Mobile) &&
                <ScheduleList mode={Mode.todayAndTomorrow}
                              lessonsData={todayTomorrowLessons}
                              style={{ marginBottom: '24px' }}
                              scheduleType={scheduleType} />}
                {getTabbedContent()}
            </div>
            {getControlsElement()}
        </div>);
    }

    const getTableLayout = () => {
        return (<div className={classes.scheduleTableContainer}>
            {getControlsElement()}
            {getTabbedContent()}
        </div>);
    }

    return viewMode === ViewOption.List
        ? getListOrMobileLayout()
        : getTableLayout();
}

export default ScheduleView;